import React, { PropsWithChildren, useCallback } from 'react'
import './index.scss'
import { WorkModel } from '@Entities/work/work'
import WorkItemPillSection from '@Components/WorkItemPillSection'
import LinkCard from '@Components/Cards/LinkCard'
import { Maybe } from '@Types'
import ExtendedWorkFields from '@Components/WorkItemV2/Catalog/CatalogExtendedWorkItem/ExtendedWorkFields'
import ExtendedWorkWriterFields from '@Components/WorkItemV2/Catalog/CatalogExtendedWorkItem/ExtendedWorkWriterFields'
import clsx from 'clsx'
import { observer } from 'mobx-react'
import Conditional from '@Components/Utility/Conditional'
import OverclaimToolTip from '@Components/Tooltips/OverclaimToolTip'

export interface ExtendedWorkFieldType {
    title: string
    value: Maybe<string>
    tooltip?: string
    className?: string
}

interface ExtendedWorkData {
    work: WorkModel
    catalogPath: string
    fields?: ExtendedWorkFieldType[]
    secondaryFields?: ExtendedWorkFieldType[]
    handleOnClick?: (work: WorkModel) => void
    showOverclaimStatus?: boolean
    bottomFields?: ExtendedWorkFieldType[]
    isBulkActionStarted?: boolean
    isBulkActionDisabled?: boolean
    selectionControl?: React.ReactNode
    openInNewTab?: boolean
    showOverclaimTooltip?: boolean
}

const ExtendedWorkItem = (props: PropsWithChildren<ExtendedWorkData>) => {
    const {
        work,
        catalogPath,
        fields,
        secondaryFields,
        showOverclaimStatus,
        bottomFields,
        isBulkActionStarted,
        isBulkActionDisabled,
        selectionControl,
        openInNewTab,
        showOverclaimTooltip,
    } = props

    const handleOnClick = useCallback(() => {
        if (work && props.handleOnClick) {
            props.handleOnClick(work)
        }
    }, [])

    return (
        <LinkCard
            className={clsx(
                'extended-work-item',
                isBulkActionDisabled && isBulkActionStarted && 'bulk-action-disabled'
            )}
            path={catalogPath}
            handleOnClick={handleOnClick}
            disabled={isBulkActionStarted}
            openInNewTab={openInNewTab}
        >
            <div className="top">
                <div className="left-data">
                    <div className="work-data">
                        <div className={clsx('work-title', isBulkActionStarted && 'bulk-action-editing')}>
                            {selectionControl}
                            <span className="work-title-text">{work.title}</span>
                        </div>
                        <ExtendedWorkFields fields={fields} defaultClassName="data-field" />
                    </div>
                    {showOverclaimTooltip && work.overclaimHeaderId ? (
                        <div className="overclaim-tool-tip" role="note" aria-label="Overclaim notice">
                            <OverclaimToolTip />
                        </div>
                    ) : (
                        <WorkItemPillSection work={work} showOverclaimStatus={!!showOverclaimStatus} />
                    )}
                </div>

                <div className="right-data">
                    {secondaryFields ? (
                        <ExtendedWorkFields fields={secondaryFields} defaultClassName="secondary-work-data" />
                    ) : (
                        <ExtendedWorkWriterFields work={work} />
                    )}
                </div>
                {props.children}
            </div>
            <Conditional conditional={!!bottomFields}>
                <div className="bottom">
                    <ExtendedWorkFields fields={bottomFields} defaultClassName="tertiary-work-data" />
                </div>
            </Conditional>
        </LinkCard>
    )
}

export default observer(ExtendedWorkItem)
